import { useRouter } from 'next/router';
import { useContext, useEffect, useRef } from 'react';
import { AppContext } from '../pages/_app';
import { NativeAppMessage, sendNativeAppMessage } from './nativeApp';
import { Route } from './routes';
import { match } from 'path-to-regexp';

const allowWebRoutingOnUrls = [
  `${Route.BUY_INSURANCES_WIZARD}?step=`,
  `${Route.CHECKOUT}?step=`,
  `${Route.FILE_CLAIM}?step=`,
];

const internalUrlsWithoutNestRouting = [
  '/api/condition/terms/',
  '/api/condition/factSheet/',
];

// See https://www.npmjs.com/package/path-to-regexp for supported patterns
export const customEvents: { endpoint: string; message: NativeAppMessage }[] = [
  {
    endpoint: '/api/condition/factSheet/:id',
    message: NativeAppMessage.OPEN_DOWNLOAD_URL,
  },
  {
    endpoint: '/api/condition/terms/:id',
    message: NativeAppMessage.OPEN_DOWNLOAD_URL,
  },
  {
    endpoint: '/api/claim/documents/:id',
    message: NativeAppMessage.OPEN_DOWNLOAD_URL,
  },
  {
    endpoint: '/api/documents/file',
    message: NativeAppMessage.OPEN_DOWNLOAD_URL,
  },
  {
    endpoint: '/api/claim/documents/:id/file',
    message: NativeAppMessage.OPEN_DOWNLOAD_URL,
  },
];

const findCustomEvent = (href: string) => {
  const matchers = customEvents.map((event) => ({
    event,
    matcher: match(event.endpoint, { end: true }), // 'end: true' ensures no trailing segments
  }));

  const localHref = href.replace(window.location.origin, '');
  const withoutQuery = localHref.split('?')[0];
  const matched = matchers.find(({ matcher }) => matcher(withoutQuery));

  return matched;
};

export const UrlChangeListener = ({ children }) => {
  const router = useRouter();
  const { isApp } = useContext(AppContext);
  const ref = useRef(router.asPath);

  // Listen for URL changes in the app using the Next.js router
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      if (isApp && ref.current !== url && ref.current !== '/') {
        if (url === '/') {
          url = Route.APP;
        }

        if (
          window.location.pathname === Route.BUY_INSURANCES_WIZARD &&
          url === Route.BASKET
        ) {
          sendNativeAppMessage(
            NativeAppMessage.DISMISS_NAVIGATION_STACK,
            Route.BASKET,
          );
        } else if (
          window.location.pathname === Route.FILE_CLAIM &&
          (url.startsWith(`${Route.FILE_CLAIM}/success/`) ||
            url === Route.FILE_CLAIM_ERROR)
        ) {
          sendNativeAppMessage(NativeAppMessage.DISMISS_NAVIGATION_STACK, url);
        } else {
          const allowWebViewRouting = allowWebRoutingOnUrls.some((allowedUrl) =>
            url.includes(allowedUrl),
          );
          if (allowWebViewRouting) {
            // Allow routing on specific URLs
            return;
          }
          const matched = findCustomEvent(url);
          if (matched) {
            // Handle custom events
            sendNativeAppMessage(matched.event.message, url);
          } else if (
            window.location.pathname.startsWith(`${Route.FILE_CLAIM}/success/`)
          ) {
            // If navigating from FILE_CLAIM success page to /app, do NOT send event
            if (url === Route.APP) {
              sendNativeAppMessage(NativeAppMessage.DISMISS_NAVIGATION_STACK);
              return;
            } else {
              sendNativeAppMessage(NativeAppMessage.LINK_CLICKED, url);
            }
          } else {
            sendNativeAppMessage(NativeAppMessage.LINK_CLICKED, url);
          }

          if (url === Route.APP) {
            sendNativeAppMessage(
              NativeAppMessage.DISMISS_NAVIGATION_STACK,
              true,
            );
          }
        }
        router.events.emit('routeChangeError');
        throw 'Navigation cancelled by native app';
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    ref.current = router.asPath;
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router.asPath]);

  // Listener for A tag clicks
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!isApp) return; // Ignore if not in the app
      if (event.target === null) return; // Ignore if target is null

      let target = (event.target as HTMLElement).closest('[href]'); // Find nearest ancestor with href
      const anchor = target as HTMLAnchorElement;

      if (!anchor) return; // Ignore if no anchor found
      const href = anchor.href;
      if (href) {
        if (
          allowWebRoutingOnUrls.some((allowedUrl) => href.includes(allowedUrl))
        ) {
          // Allow routing on specific URLs
          return;
        }
        const matched = findCustomEvent(href);
        event.preventDefault();
        if (href.startsWith('mailto:')) {
          sendNativeAppMessage(NativeAppMessage.MAILTO_CLICKED, href);
        } else if (href.startsWith('tel:')) {
          sendNativeAppMessage(NativeAppMessage.PHONE_CLICKED, href);
        } else if (matched) {
          // Handle custom events
          sendNativeAppMessage(matched.event.message, href);
        } else if (href.startsWith(window.location.origin)) {
          // Internal routes are handled by Next.js router
          // Certain internal URLs are however not handled by Next.js router
          // and need to be handled manually here
          const triggerInternalRoutingMessage =
            internalUrlsWithoutNestRouting.some((allowedUrl) =>
              href.includes(allowedUrl),
            );
          if (triggerInternalRoutingMessage) {
            sendNativeAppMessage(NativeAppMessage.LINK_CLICKED, href);
          }
        } else {
          sendNativeAppMessage(NativeAppMessage.OPEN_EXTERNAL_URL, href);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []); // Empty dependency array to run only once on mount

  return <>{children}</>;
};
